import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { questionClassesQueryOptions } from '../api/question.api';
import { useSaveQuestionProject } from '../hooks/useQuestionMutations';
import {
  questionProjectFormSchema,
  type QuestionProject,
  type QuestionProjectFormValues,
} from '../types/question.types';

const DEFAULTS: QuestionProjectFormValues = {
  name: '',
  isActive: true,
};

const toDateInput = (value: string | null): string | undefined => {
  if (value === null) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? undefined
    : `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(
        parsed.getDate(),
      ).padStart(2, '0')}`;
};

interface QuestionProjectFormModalProps {
  project: QuestionProject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuestionProjectFormModal({
  project,
  open,
  onOpenChange,
}: QuestionProjectFormModalProps) {
  const { data: classes, isLoading: classesLoading } = useQuery(questionClassesQueryOptions());

  const form = useForm<QuestionProjectFormValues>({
    resolver: zodResolver(questionProjectFormSchema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    form.reset(
      project === null
        ? DEFAULTS
        : {
            name: project.name,
            isActive: project.isActive,
            ...(project.classId === null ? {} : { classId: project.classId }),
            ...(toDateInput(project.declareDate) === undefined
              ? {}
              : { declareDate: toDateInput(project.declareDate) }),
            ...(toDateInput(project.deadlineDate) === undefined
              ? {}
              : { deadlineDate: toDateInput(project.deadlineDate) }),
          },
    );
  }, [project, open, form]);

  const save = useSaveQuestionProject(() => onOpenChange(false));

  const classOptions = (classes ?? []).map((item) => ({
    id: item.id,
    name: item.name,
  }));

  function submit(values: QuestionProjectFormValues) {
    save.mutate({
      id: project?.projectId ?? null,
      payload: {
        name: values.name,
        isActive: values.isActive,
        ...(values.classId === undefined ? {} : { classId: values.classId }),
        ...(values.declareDate === undefined ? {} : { declareDate: values.declareDate }),
        ...(values.deadlineDate === undefined ? {} : { deadlineDate: values.deadlineDate }),
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{project === null ? 'New Project' : 'Edit Project'}</DialogTitle>
          <DialogDescription>
            A collection round: the class it covers, when it opened and when the papers are due.
            Leaving a date out clears it.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="SSC Board Question Collection-2026"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="classId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Class</FormLabel>
                  <FormControl>
                    <OptionSelect
                      id="qpf-class"
                      options={classOptions}
                      isLoading={classesLoading}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Pick a class"
                      clearable
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="declareDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Declared</FormLabel>
                    <FormControl>
                      <DatePickerInput
                        id="qpf-declare"
                        value={field.value ?? ''}
                        onChange={(next) => field.onChange(next === '' ? undefined : next)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="deadlineDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deadline</FormLabel>
                    <FormControl>
                      <DatePickerInput
                        id="qpf-deadline"
                        value={field.value ?? ''}
                        onChange={(next) => field.onChange(next === '' ? undefined : next)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={field.value ? 'default' : 'outline'}
                        onClick={() => field.onChange(true)}
                      >
                        Active
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={field.value ? 'outline' : 'default'}
                        onClick={() => field.onChange(false)}
                      >
                        Inactive
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                {project === null ? 'Create' : 'Update'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
